/**
 * MFX OS — OS Command (blueprint §8 daily queues, CSR slice). Live counts of
 * what needs a human right now, plus the append-only activity river.
 */
import { useNavigate } from 'react-router-dom';
import { useOsList, ago } from '../../os/core';
import { Chip } from '../../os/ui';
import type { CustomerPO, JobTicketOS, OsEvent, ProductionJob, SalesOrderOS } from '../../os/types';

export default function OsCommand() {
  const nav = useNavigate();
  const { rows: pos } = useOsList<CustomerPO>('customerPOs', { limit: 400 });
  const { rows: sos } = useOsList<SalesOrderOS>('salesOrders', { wheres: [['source', '==', 'mfx-os']], limit: 400 });
  const { rows: tickets } = useOsList<JobTicketOS>('jobTickets', { wheres: [['source', '==', 'mfx-os']], limit: 400 });
  const { rows: jobs } = useOsList<ProductionJob>('productionJobs', { limit: 400 });
  const { rows: events } = useOsList<OsEvent>('osEvents', { order: ['at', 'desc'], limit: 40 });

  const q = {
    newPos: pos.filter((x) => x.status === 'RECEIVED' || x.status === 'REVIEW_REQUIRED').length,
    exceptions: pos.filter((x) => x.status === 'EXCEPTION' || !!x.exception).length,
    waiting: pos.filter((x) => x.status === 'WAITING_ON_CLIENT').length,
    approvedNoSo: pos.filter((x) => x.status === 'APPROVED' && !x.salesOrderId).length,
    ackPending: sos.filter((x) => x.ack && !x.ack.sentAt).length,
    unresolvedSos: sos.filter((x) => !x.jobTicketIds?.length).length,
    blockedJobs: jobs.filter((x) => x.readiness === 'blocked').length,
    readyJobs: jobs.filter((x) => x.readiness === 'ready').length,
    openTickets: tickets.length,
    blockedTickets: tickets.filter((x) => (x.blockedCount || 0) > 0).length,
  };

  const CARDS: { label: string; value: number; note: string; color: string; to: string }[] = [
    { label: 'POs to review', value: q.newPos, note: 'received + review required', color: 'var(--yl)', to: '/os/pos' },
    { label: 'Waiting on client', value: q.waiting, note: 'clarifications out', color: 'var(--ac)', to: '/os/pos' },
    { label: 'Approved, no SO', value: q.approvedNoSo, note: 'one click from a sales order', color: 'var(--or)', to: '/os/pos' },
    { label: 'Acks to send', value: q.ackPending, note: 'acknowledgments drafted', color: 'var(--or)', to: '/os/sos' },
    { label: 'SOs without ticket', value: q.unresolvedSos, note: 'execution not created', color: 'var(--yl)', to: '/os/sos' },
    { label: 'Blocked jobs', value: q.blockedJobs, note: 'gates with blockers', color: 'var(--rd)', to: '/os/tickets' },
    { label: 'Jobs fully green', value: q.readyJobs, note: 'schedulable', color: 'var(--gn)', to: '/os/tickets' },
    { label: 'Open tickets', value: q.openTickets, note: `${q.blockedTickets} with blockers`, color: 'var(--ac)', to: '/os/tickets' },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">OS Command</div>
          <div className="page-sub">The PO → SO → Ticket → Job spine, exception-first. Every number is a queue, not a report.</div>
        </div>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 18 }}>
        {CARDS.map((c) => (
          <button key={c.label} className="metric-card os-click" onClick={() => nav(c.to)}>
            <div className="metric-label">{c.label}</div>
            <div className="metric-value" style={{ color: c.value ? c.color : 'var(--tx3)' }}>{c.value}</div>
            <div className="metric-note">{c.note}</div>
          </button>
        ))}
      </div>

      <div className="card os-card">
        <div className="os-sect" style={{ marginTop: 0 }}>Live activity — append-only, actor-stamped</div>
        {!events.length && <div className="empty">No OS-chain activity yet. Log the first PO from the inbox.</div>}
        <div className="os-timeline">
          {events.map((e, i) => (
            <div key={i} className="os-tl-row">
              <div className="os-tl-time">{ago(e.at)}</div>
              <div className="os-tl-dot" style={{ background: 'var(--ac)' }} />
              <div className="os-tl-body">
                <div className="os-tl-action"><Chip label={e.displayId} tone="var(--ac)" /> {e.action}{e.reason ? <span className="os-tl-reason"> — {e.reason}</span> : null}</div>
                <div className="os-tl-actor">{e.actor?.name || e.actor?.email}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
